import { useState, useEffect, useRef } from 'react';
import {
  fetchLocations,
  fetchLocationBySlug,
  fetchLocationsByCountry,
  fetchCountries,
  fetchCaseStudies,
  fetchCaseStudyBySlug,
  fetchServices,
  fetchServiceBySlug,
  fetchProjects,
} from '../lib/cmsData';
import type { Location } from '../data/locations';
import type { CaseStudy } from '../data/caseStudies';
import type { Service } from '../data/services';

interface AsyncState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

function useAsync<T>(fetcher: () => Promise<T>, initialValue: T, dep: string): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: initialValue, loading: true, error: null });
  const prevDep = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevDep.current === dep) return;
    prevDep.current = dep;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetcher()
      .then((data) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((err) => { if (!cancelled) setState((s) => ({ ...s, loading: false, error: err.message })); });
    return () => { cancelled = true; };
  }, [dep]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

export function useLocations() {
  return useAsync<Location[]>(fetchLocations, [], 'locations');
}

export function useLocationBySlug(slug: string) {
  return useAsync<Location | undefined>(() => fetchLocationBySlug(slug), undefined, `location:${slug}`);
}

export function useLocationsByCountry(countrySlug: string) {
  return useAsync<Location[]>(() => fetchLocationsByCountry(countrySlug), [], `country-locations:${countrySlug}`);
}

export function useCountries() {
  return useAsync<{ slug: string; name: string }[]>(fetchCountries, [], 'countries');
}

export function useCaseStudies() {
  return useAsync<CaseStudy[]>(fetchCaseStudies, [], 'case-studies');
}

export function useCaseStudyBySlug(slug: string) {
  return useAsync<CaseStudy | undefined>(() => fetchCaseStudyBySlug(slug), undefined, `case-study:${slug}`);
}

export function useServices() {
  return useAsync<Service[]>(fetchServices, [], 'services');
}

export function useServiceBySlug(slug: string) {
  return useAsync<Service | undefined>(() => fetchServiceBySlug(slug), undefined, `service:${slug}`);
}

export function useProjects() {
  return useAsync(fetchProjects, [], 'projects');
}
